// Phase H.13c — Variable validator side panel.
//
// Read-only right panel that lists every unique merge tag found in the
// editor (body + header + footer combined), grouped by source group, with
// occurrence counts. A bottom red-badged section flags any `{{...}}`
// substrings that are NOT in the merge-tag registry (typos / stale refs).
//
// State is re-derived from the current HTML each render — closing &
// reopening the panel naturally yields a fresh view; nothing persists.
//
// Clicking a row scrolls the first matching chip in the editor into view
// and applies a 300ms pulse animation to draw the eye.
//
// Panel is intentionally non-modal (no backdrop, no scroll-lock) so it
// can coexist with the editor and the version-history drawer. Both
// drawer & validator render at z-50; when both are toggled on the
// version drawer (which is full-screen modal) visually overlays the
// validator, but their state is independent and toggling one never
// closes the other.

import React, { useMemo } from "react";
import { X, ListChecks, AlertTriangle } from "lucide-react";

const TAG_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

function findOccurrences(combinedHtml) {
    // Returns Map<tag, count> across the combined HTML.
    const counts = new Map();
    if (!combinedHtml) return counts;
    // Reset regex lastIndex (it's a module-level RegExp with /g).
    TAG_RE.lastIndex = 0;
    let m;
    while ((m = TAG_RE.exec(combinedHtml)) !== null) {
        const tag = m[1];
        counts.set(tag, (counts.get(tag) || 0) + 1);
    }
    return counts;
}

function safeSelector(tag) {
    // CSS.escape might not exist in older runtimes; the tag charset is
    // [A-Za-z0-9_.] which is already CSS-attr-value safe, but we still
    // use CSS.escape when available.
    if (typeof CSS !== "undefined" && CSS && typeof CSS.escape === "function") {
        return CSS.escape(tag);
    }
    return tag.replace(/"/g, '\\"');
}

export default function VariableValidatorPanel({
    open,
    onClose,
    bodyHtml,
    headerHtml,
    footerHtml,
    tagRegistry,
    editorWrapRef,
}) {
    // The brief: re-derive from doc on each open — using useMemo keyed on
    // the inputs gives us that without persisting any state on close.
    const knownMap = useMemo(() => {
        const map = new Map();
        (tagRegistry || []).forEach((t) => map.set(t.tag, t));
        return map;
    }, [tagRegistry]);

    const { known, unknown, totalOccurrences } = useMemo(() => {
        if (!open) return { known: [], unknown: [], totalOccurrences: 0 };
        const combined = `${bodyHtml || ""}\n${headerHtml || ""}\n${footerHtml || ""}`;
        const counts = findOccurrences(combined);
        const knownArr = [];
        const unknownArr = [];
        let total = 0;
        for (const [tag, count] of counts.entries()) {
            total += count;
            if (knownMap.has(tag)) {
                const meta = knownMap.get(tag);
                knownArr.push({
                    tag,
                    count,
                    label: meta.label || tag,
                    group: meta.group || "Other",
                });
            } else {
                unknownArr.push({ tag, count });
            }
        }
        knownArr.sort(
            (a, b) =>
                (a.group || "").localeCompare(b.group || "") ||
                a.tag.localeCompare(b.tag)
        );
        unknownArr.sort((a, b) => a.tag.localeCompare(b.tag));
        return { known: knownArr, unknown: unknownArr, totalOccurrences: total };
    }, [open, bodyHtml, headerHtml, footerHtml, knownMap]);

    const groupedKnown = useMemo(() => {
        return known.reduce((acc, t) => {
            (acc[t.group] = acc[t.group] || []).push(t);
            return acc;
        }, {});
    }, [known]);

    const handleLocate = (tag) => {
        const root = editorWrapRef?.current;
        if (!root) return;
        const chip = root.querySelector(`[data-merge-tag="${safeSelector(tag)}"]`);
        if (chip) {
            try {
                chip.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch (_) {
                chip.scrollIntoView();
            }
            chip.classList.add("tt-merge-tag-pulse");
            window.setTimeout(() => {
                chip.classList.remove("tt-merge-tag-pulse");
            }, 350);
        }
    };

    if (!open) return null;

    return (
        <aside
            data-testid="variable-validator-panel"
            className="fixed top-0 right-0 h-screen w-[360px] max-w-full z-50 bg-white border-l border-slate-200 shadow-xl flex flex-col"
        >
            <header className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-2">
                <div>
                    <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
                        <ListChecks size={16} className="text-blue-800" />
                        Merge tags in this document
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5"
                        data-testid="variable-validator-summary">
                        {known.length} known, {unknown.length} unknown · {totalOccurrences} total uses
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="variable-validator-close"
                    aria-label="Close variable validator"
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                >
                    <X size={14} />
                </button>
            </header>

            <div className="flex-1 overflow-y-auto p-3" data-testid="variable-validator-body">
                {known.length === 0 && unknown.length === 0 && (
                    <p className="text-sm text-slate-500"
                        data-testid="variable-validator-empty">
                        No merge tags found in this document yet. Click a tag from
                        the right rail to insert one.
                    </p>
                )}

                {Object.entries(groupedKnown).map(([group, tags]) => (
                    <section key={group} className="mb-4" data-testid={`vv-group-${group}`}>
                        <div className="text-[10px] font-medium tracking-wider uppercase text-slate-500 mb-1.5">
                            {group}
                        </div>
                        <ul className="space-y-1">
                            {tags.map((t) => (
                                <li key={t.tag}>
                                    <button
                                        type="button"
                                        data-testid={`vv-row-${t.tag}`}
                                        onClick={() => handleLocate(t.tag)}
                                        className="w-full text-left px-2 py-1.5 rounded-md border border-transparent hover:bg-slate-50 hover:border-slate-200 flex items-center justify-between gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-xs font-mono text-slate-900 truncate">
                                                {`{{${t.tag}}}`}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {t.label}
                                            </div>
                                        </div>
                                        <span
                                            data-testid={`vv-count-${t.tag}`}
                                            className="shrink-0 text-[10px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full tabular-nums"
                                        >
                                            Used {t.count} {t.count === 1 ? "time" : "times"}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}

                {unknown.length > 0 && (
                    <section
                        data-testid="variable-validator-unknown-section"
                        className="mt-2 border-t border-red-200 pt-3"
                    >
                        <div className="text-[10px] font-medium tracking-wider uppercase text-red-700 mb-1.5 flex items-center gap-1">
                            <AlertTriangle size={11} /> Unknown / typos
                        </div>
                        <p className="text-[11px] text-red-600 mb-2">
                            These <code className="font-mono">{"{{...}}"}</code> tokens
                            aren't in the merge-tag registry. Likely typos or stale
                            references — fix or remove before sending the contract.
                        </p>
                        <ul className="space-y-1">
                            {unknown.map((t) => (
                                <li key={t.tag}>
                                    <button
                                        type="button"
                                        data-testid={`vv-unknown-${t.tag}`}
                                        onClick={() => handleLocate(t.tag)}
                                        className="w-full text-left px-2 py-1.5 rounded-md border border-red-200 bg-red-50/60 hover:bg-red-50 flex items-center justify-between gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-xs font-mono text-red-800 truncate">
                                                {`{{${t.tag}}}`}
                                            </div>
                                            <div className="text-[11px] text-red-600">
                                                Not in registry
                                            </div>
                                        </div>
                                        <span className="shrink-0 text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full tabular-nums">
                                            {t.count} {t.count === 1 ? "use" : "uses"}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        </aside>
    );
}
